import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import styled from 'styled-components';
import { clubService } from '../../services/clubService';
import { Button, Card, Input, Label, ErrorMessage } from '../../styles/GlobalStyles';
import { LoadingSpinner } from '../../components/LoadingSpinner';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--background-color);
  padding: var(--spacing-md);
`;

const InvitationCard = styled(Card)`
  max-width: 500px;
  width: 100%;
  padding: var(--spacing-xl);
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: var(--spacing-xs);
  text-align: center;
`;

const Subtitle = styled.p`
  color: var(--text-secondary);
  text-align: center;
  margin-bottom: var(--spacing-xl);
  font-size: 0.875rem;
`;

const InvitationInfo = styled.div`
  background: var(--primary-color)10;
  border: 1px solid var(--primary-color)30;
  border-radius: var(--border-radius);
  padding: var(--spacing-lg);
  margin-bottom: var(--spacing-xl);
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: var(--spacing-sm);
  font-size: 0.875rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoLabel = styled.span`
  color: var(--text-secondary);
  font-weight: 500;
`;

const InfoValue = styled.span`
  color: var(--text-primary);
  font-weight: 600;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-md);

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
`;

const PasswordRequirements = styled.div`
  font-size: 0.75rem;
  color: var(--text-secondary);
  margin-top: var(--spacing-xs);
  
  ul {
    margin: var(--spacing-xs) 0;
    padding-left: var(--spacing-lg);
  }
  
  li {
    margin-bottom: var(--spacing-xs);
  }
`;

const LinkText = styled.div`
  text-align: center;
  margin-top: var(--spacing-lg);
  font-size: 0.875rem;
  color: var(--text-secondary);

  a {
    color: var(--primary-color);
    text-decoration: none;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const ErrorCard = styled.div`
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
  padding: var(--spacing-lg);
  border-radius: var(--border-radius);
  margin-bottom: var(--spacing-lg);
  text-align: center;
  
  h2 {
    margin: 0 0 var(--spacing-sm) 0;
    font-size: 1.25rem;
  }
  
  p {
    margin: var(--spacing-sm) 0;
  }
`;

const schema = yup.object({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  email: yup.string().email('Invalid email').required('Email is required'),
  password: yup.string()
    .min(8, 'Password must be at least 8 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character (@$!%*?&)'
    )
    .required('Password is required'),
  confirmPassword: yup.string()
    .oneOf([yup.ref('password')], 'Passwords must match')
    .required('Please confirm your password'),
});

type AcceptInvitationFormData = yup.InferType<typeof schema>;

export const AdminAcceptInvitationPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [invitationData, setInvitationData] = useState<any>(null);
  const [validationError, setValidationError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState<string>('');

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<AcceptInvitationFormData>({
    resolver: yupResolver(schema),
  });

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setValidationError('No invitation token provided.');
        setIsValidating(false);
        return;
      }

      try {
        const response = await clubService.validateInvitation(token);
        
        if (response.valid && response.invitation) {
          setIsValid(true);
          setInvitationData(response.invitation);
          
          // Pre-fill form with invitation data
          setValue('firstName', response.invitation.invitedFirstName);
          setValue('lastName', response.invitation.invitedLastName);
          setValue('email', response.invitation.invitedEmail);
        } else {
          setIsValid(false);
          
          // Handle specific error codes
          const errorCode = response.error?.code;
          if (errorCode === 'INVITATION_EXPIRED') {
            setValidationError('This invitation has expired. Please contact your club administrator for a new invitation.');
          } else if (errorCode === 'INVITATION_ALREADY_USED') {
            setValidationError('This invitation has already been used. If you have an account, please sign in.');
          } else if (errorCode === 'INVITATION_REVOKED') {
            setValidationError('This invitation has been revoked. Please contact your club administrator.');
          } else {
            setValidationError(response.error?.message || 'Invalid invitation token.');
          }
        }
      } catch (error: any) {
        setIsValid(false);
        setValidationError('Failed to validate invitation. Please try again.');
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token, setValue]);

  const onSubmit = async (data: AcceptInvitationFormData) => {
    if (!token) return;

    setIsLoading(true);
    setServerError('');
    
    try {
      // Accept the invitation
      const response = await clubService.acceptInvitation({
        token,
        email: data.email,
        password: data.password,
        firstName: data.firstName,
        lastName: data.lastName,
      });
      
      // Store the token and log in
      localStorage.setItem('token', response.token);
      localStorage.setItem('currentUserEmail', data.email);
      
      // Redirect based on role
      if (response.user.role === 'CLUB_ADMIN') {
        // Check if user needs to complete club setup
        if (!response.user.profile.clubId) {
          navigate('/administration-club/setup');
        } else {
          navigate('/administration-club/dashboard');
        }
      } else {
        // Other roles go to main dashboard
        navigate('/dashboard');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error?.message;
      const errorCode = error.response?.data?.error?.code;
      
      if (errorCode === 'EMAIL_ALREADY_EXISTS') {
        setServerError('An account with this email already exists. Please sign in to link your account.');
      } else if (errorCode === 'INVITATION_EXPIRED') {
        setServerError('This invitation has expired. Please contact your club administrator.');
      } else if (errorCode === 'INVITATION_ALREADY_USED') {
        setServerError('This invitation has already been used.');
      } else {
        setServerError(errorMessage || 'Failed to accept invitation. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading spinner while validating
  if (isValidating) {
    return (
      <Container>
        <InvitationCard>
          <LoadingSpinner />
          <p style={{ textAlign: 'center', marginTop: 'var(--spacing-md)', color: 'var(--text-secondary)' }}>
            Validating invitation...
          </p>
        </InvitationCard>
      </Container>
    );
  }

  // Show error if token is invalid
  if (!isValid) {
    return (
      <Container>
        <InvitationCard>
          <Title>Invalid Invitation</Title>
          <ErrorCard>
            <h2>⚠️ Unable to Accept Invitation</h2>
            <p>{validationError}</p>
          </ErrorCard>
          <LinkText>
            <Link to="/administration-club">Go to Sign In</Link>
          </LinkText>
        </InvitationCard>
      </Container>
    );
  }

  return (
    <Container>
      <InvitationCard>
        <Title>Accept Invitation</Title>
        <Subtitle>Complete your registration to join {invitationData?.clubName}</Subtitle>

        <InvitationInfo>
          <InfoRow>
            <InfoLabel>Club:</InfoLabel>
            <InfoValue>{invitationData?.clubName}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Role:</InfoLabel>
            <InfoValue>{invitationData?.invitedRole.replace('_', ' ')}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Expires:</InfoLabel>
            <InfoValue>
              {new Date(invitationData?.expiresAt).toLocaleDateString()}
            </InfoValue>
          </InfoRow>
        </InvitationInfo>

        <Form onSubmit={handleSubmit(onSubmit)}>
          <FormRow>
            <FormGroup>
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                type="text"
                {...register('firstName')}
                className={errors.firstName ? 'error' : ''}
              />
              {errors.firstName && <ErrorMessage>{errors.firstName.message}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                type="text"
                {...register('lastName')}
                className={errors.lastName ? 'error' : ''}
              />
              {errors.lastName && <ErrorMessage>{errors.lastName.message}</ErrorMessage>}
            </FormGroup>
          </FormRow>

          <FormGroup>
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              {...register('email')}
              className={errors.email ? 'error' : ''}
              readOnly
              style={{ backgroundColor: 'var(--background-color)' }}
            />
            {errors.email && <ErrorMessage>{errors.email.message}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              {...register('password')}
              className={errors.password ? 'error' : ''}
              placeholder="Create a strong password"
              autoComplete="new-password"
            />
            <PasswordRequirements>
              Password must contain:
              <ul>
                <li>At least 8 characters</li>
                <li>One uppercase letter (A-Z)</li>
                <li>One lowercase letter (a-z)</li>
                <li>One number (0-9)</li>
                <li>One special character (@$!%*?&)</li>
              </ul>
            </PasswordRequirements>
            {errors.password && <ErrorMessage>{errors.password.message}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <Input
              id="confirmPassword"
              type="password"
              {...register('confirmPassword')}
              className={errors.confirmPassword ? 'error' : ''}
              placeholder="Re-enter your password"
              autoComplete="new-password"
            />
            {errors.confirmPassword && <ErrorMessage>{errors.confirmPassword.message}</ErrorMessage>}
          </FormGroup>

          {serverError && <ErrorMessage>{serverError}</ErrorMessage>}

          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'Accepting Invitation...' : 'Accept Invitation & Create Account'}
          </Button>
        </Form>

        <LinkText>
          Already have an account?{' '}
          <Link to="/administration-club">Sign in to link your account</Link>
        </LinkText>
      </InvitationCard>
    </Container>
  );
};
